/**
 * Reference healing over a `DrawingDocument` (PR1b — pure referential repair).
 *
 * `healDrawingReferences(document)` is a PURE function that takes a drawing
 * whose cross-object references may dangle (a legal-but-dirty AutoCAD file —
 * dangling layer / purged text style / deleted linetype) and returns a NEW
 * document in which every HEALABLE dangling reference is substituted for the
 * always-present built-in default, the list of `ReferenceFix` applied, and
 * the POST-heal `validateDrawingReferences` report so the caller can see what
 * could NOT be auto-repaired.
 *
 * It NEVER mutates the input (structuredClone) and NEVER throws (plan §12 /
 * edge case #1: a legal dirty DXF must still load — heal only normalises the
 * initial state, it never rejects). Healing is initial-state normalisation,
 * NOT a Command-Engine mutation (plan §12 / P2-5) — it runs once at load,
 * before the document is handed to the editor, so it carries no undo entry.
 *
 * ── Substitution rules (AutoCAD AUDIT/RECOVER + ezdxf `Auditor` semantics) ──
 * AutoCAD's AUDIT and RECOVER repair dangling symbol-table references by
 * re-pointing them at the reserved default that is GUARANTEED to exist in every
 * drawing; ezdxf's `Auditor` does the same (`auditor.fixed_errors`). We mirror
 * that table exactly:
 *
 *   - layer                → "0"          (the always-present default layer;
 *                                          AutoCAD: layer 0 can never be purged)
 *   - text style           → "Standard"   (default STYLE — always present)
 *   - dimension style      → "Standard"   (default DIMSTYLE — always present)
 *   - multileader style    → "Standard"   (default MLEADERSTYLE — the seed
 *                                          default name in model.ts)
 *   - linetype             → "Continuous" (reserved LTYPE — always present)
 *   - annotation scale     → "1:1"        (built-in CANNOSCALE — always present)
 *
 *   - block (INSERT.blockName, dimension anonymousBlockName, mleader blockName,
 *     style-table DIMBLK/arrowhead/blockName) → UNFIXABLE. A missing BLOCK
 *     definition cannot be invented: substituting some other block would change
 *     the drawing's geometry, and there is no "empty block" default that is safe
 *     across every call site (an arrowhead default ≠ a content-block default).
 *     We leave the reference verbatim and KEEP it as an error in the returned
 *     report (we do NOT drop the entity, we do NOT throw). AutoCAD likewise
 *     leaves an unresolved INSERT as an "unresolved block reference" rather than
 *     fabricating geometry.
 *
 *   - handle refs (ownerHandle / ownerBlockRecordHandle / layoutHandle /
 *     annotativeDecompositions value / mleader attDefHandle) → OUT OF SCOPE for
 *     heal (report-only). A handle points at a specific object instance; there
 *     is no reserved "default handle" to substitute, so re-pointing would be a
 *     guess. These remain errors in the report.
 *
 *   - annotativeDecompositions KEY (a scale name stored as a Record KEY on a
 *     block record) → OUT OF SCOPE for heal (report-only). Healing it would
 *     require RENAMING a map key (and merging on collision), which is a
 *     structural rewrite, not a value substitution. Deferred to Phase 2 wiring.
 *
 * Healing is driven off `validateDrawingReferences(document).errors`: for every
 * error whose `code` is in the healable set AND whose `path` is navigable, the
 * exact value at that path is rewritten on the CLONED document. Matching is
 * value-substitution only — the audit already did the case-insensitive
 * resolution, so an error in the list is, by construction, a dangling ref.
 *
 * PERF NOTE (Phase-2 follow-up): Phase-1 uses a single `structuredClone` of the
 * whole document for correctness. For very large drawings (685k entities) this
 * is wasteful — only the touched containers need cloning. The wiring PR should
 * switch to a per-container shallow-immutable copy down the touched path. The
 * heal API surface (inputs / outputs) does not change.
 *
 * Source of truth: docs/superpowers/plans/2026-05-30-drawing-object-graph.md
 * §12 (PR1b) / §5.3 / edge case #1 ("Legal dirty DXF").
 */

import {
  buildDefaultStandardDimensionStyle,
  buildDefaultStandardMultileaderStyle,
  buildDefaultStandardTextStyle,
} from "./dxfBridge.drawingToDxf";
import type { DrawingDocument } from "./model";
import { validateDrawingReferences } from "./referenceAudit";
import {
  ReferenceErrorCode,
  type ReferenceAuditReport,
  type ReferenceFix,
  type ReferenceIssue,
} from "./referenceAudit.types";
import {
  applyHealIssue,
  ensureHealDefaultTableRecord,
  isHealableIssue,
} from "./referenceHeal.rules";

/* ────────────────────────────────────────────────────────────────────────
 * Public API.
 * ──────────────────────────────────────────────────────────────────────── */

/**
 * Heal every HEALABLE dangling reference in `document`. PURE — never mutates
 * the input (it clones) and NEVER throws (a dirty-but-legal file is healed as
 * far as possible and the remainder is reported, never rejected).
 *
 * @returns
 *  - `document`: a NEW document with healable refs substituted.
 *  - `fixes`: one `ReferenceFix` per applied substitution (empty for a clean
 *    document — no spurious rewrites).
 *  - `report`: the POST-heal `validateDrawingReferences` report — `ok: true`
 *    iff every reference now resolves; any UNFIXABLE refs (missing blocks,
 *    dangling handles) remain in `report.errors`.
 */
export function healDrawingReferences(document: DrawingDocument): {
  document: DrawingDocument;
  fixes: ReferenceFix[];
  report: ReferenceAuditReport;
} {
  // 1. Audit the ORIGINAL document to discover dangling references.
  const preReport = validateDrawingReferences(document);

  // Nothing to heal — return the input verbatim (referentially identical so a
  // clean doc deep-equals the input; no spurious structuredClone churn).
  const healable = preReport.errors.filter(isHealableIssue);
  if (healable.length === 0) {
    return { document, fixes: [], report: preReport };
  }

  // 2. Clone so the input is never mutated (edge case #1 — purity).
  const next = structuredClone(document);

  // 3. Apply each healable substitution at its exact path on the clone.
  //    `applyHealIssue` is the SHARED rule-application primitive (single source
  //    of truth with `healEntityRefs`); whole-doc heal supplies the owner root.
  const fixes: ReferenceFix[] = [];
  for (const issue of healable) {
    const root = resolveOwnerRoot(next, issue);
    if (root === undefined) continue; // owner not locatable — leave as error
    const fix = applyHealIssue(root, issue);
    if (fix) fixes.push(fix);
  }

  // 4. Guarantee the always-present built-in target record exists (Codex#PR1b
  //    P2). When a text/dimension/multileader style ref heals to "Standard" but
  //    the (non-empty) STYLE / DIMSTYLE / MLEADERSTYLE table OMITS "Standard"
  //    (a legal-but-dirty DXF — AutoCAD always has it), the substituted ref
  //    would STILL dangle. `importStyleTables` only seeds "Standard" for the
  //    EMPTY-table case; the multileader path always seeds. We mirror that here
  //    on the CLONED doc so the post-heal audit is clean and export carries no
  //    dangling style ref. Layer "0" / linetype "Continuous" / scale "1:1" are
  //    seeded by the importers/schema and audited as always-present, so only the
  //    style-record codes need this fixup (see `ensureHealDefaultTableRecord`).
  for (const fix of fixes) {
    ensureHealDefaultTableRecord(next.styleTables, fix.code, {
      textStyle: buildDefaultStandardTextStyle,
      dimensionStyle: buildDefaultStandardDimensionStyle,
      multileaderStyle: buildDefaultStandardMultileaderStyle,
    });
  }

  // 5. Re-audit the healed document so callers see what could NOT be healed
  //    (missing blocks, dangling handles, map-key scale refs).
  const report = validateDrawingReferences(next);
  return { document: next, fixes, report };
}

/**
 * Export gate (plan §12 — "warn-only audit call"). A thin, intent-documenting
 * alias of `validateDrawingReferences`: callers invoke this BEFORE an export to
 * obtain a REPORT of unresolved references and decide for themselves whether to
 * warn the user or block the export. It is deliberately report-only — it never
 * throws. Strict callers that want a throw use `assertNoReferenceErrors`.
 *
 * Kept as a named wrapper (not a bare re-export) so the call site reads
 * `auditDocumentForExport(doc)` and the "warn-only before export" intent is
 * self-evident at the use site.
 */
export function auditDocumentForExport(
  document: DrawingDocument,
): ReferenceAuditReport {
  return validateDrawingReferences(document);
}

/* ────────────────────────────────────────────────────────────────────────
 * Internals.
 * ──────────────────────────────────────────────────────────────────────── */

/**
 * Locate the mutable owner record on the CLONED document that holds the
 * offending value, so `setByPath` can rewrite the `path` RELATIVE to it.
 *
 * The audit's `path` is owner-relative (e.g. an entity error's path is
 * "layer" / "frozenLayers[0]", NOT "spaces[0].entities[3].layer"), so we first
 * resolve the owner from `(ownerKind, container, ownerId)` and hand `setByPath`
 * the owner object as the navigation root. Document-level issues
 * (cannoscaleName) navigate from the document itself.
 */
function resolveOwnerRoot(
  doc: DrawingDocument,
  issue: ReferenceIssue,
): Record<string, unknown> | undefined {
  switch (issue.ownerKind) {
    case "entity": {
      // Codex #PR1b P2 FIX-1 — respect the owner KIND before searching.
      // An `entity` issue's `container` is ALWAYS a SPACE id (the audit emits
      // `kind: "entity"` only for `document.spaces`; see validateDrawingReferences).
      // Resolve the container directly via `doc.spaces` and DO NOT cross-search
      // `doc.blocks`: a block definition may legally share the NAME of a space id
      // (a custom block named `model`) and hold an entity with the same id, which
      // a fall-through block search would patch by mistake — healing the wrong
      // entity while the real dangling ref survives the post-heal audit.
      const space = doc.spaces.find((s) => s.id === issue.container);
      if (!space) return undefined;
      const e = space.entities.find((x) => x.id === issue.ownerId);
      return e as unknown as Record<string, unknown> | undefined;
    }
    case "blockChild": {
      // Codex #PR1b P2 FIX-1 — a `blockChild` issue's `container` is ALWAYS a
      // block-definition name (the audit emits `kind: "blockChild"` only for
      // `document.blocks`). Resolve the container directly via `doc.blocks` and
      // DO NOT cross-search `doc.spaces` first: when a space id collides with a
      // block name, the space search would resolve (and patch) the wrong entity.
      const block = issue.container ? doc.blocks[issue.container] : undefined;
      if (!block) return undefined;
      const e = block.entities.find((x) => x.id === issue.ownerId);
      return e as unknown as Record<string, unknown> | undefined;
    }
    case "styleTableRecord": {
      // ownerId is the record NAME; `field` tells which table it lives in.
      //
      // Built-in records share the name "Standard" across dimension styles,
      // text styles AND multileader styles. A name-search across ALL tables
      // matches the FIRST "Standard" (the dimension style), so a dangling
      // multileader-style field (`leaderLineType` / `textStyleName`) would be
      // healed on the WRONG record and the real dangling ref would survive
      // (Codex #PR1b P2). Resolve the table AUTHORITATIVELY from the emitting
      // field — every style-table audit field maps to exactly one table —
      // then look the record up by name WITHIN that one table only.
      const table = styleTableForField(doc, issue.field);
      if (!table) return undefined; // unknown field — leave as reported error
      // Codex #PR1b P2 FIX-1 — a value-substitution heal resolves the owner by
      // NAME (`.find`), which returns the FIRST record bearing that name. When
      // the table has DUPLICATE names (a DUPLICATE_TABLE_ENTRY_NAME error in its
      // own right), `.find` may patch a CLEAN record while the record that
      // actually emitted the dangling-ref error survives unhealed — a silent
      // WRONG heal. The owner is ambiguous, so SKIP the value heal entirely and
      // leave the dangling ref as a reported error. Case-insensitive: table
      // names collide case-insensitively in AutoCAD (Layer "A" == "a").
      const ownerKey = issue.ownerId?.toLowerCase();
      let nameMatches = 0;
      for (const r of table) {
        if (r.name.toLowerCase() === ownerKey) nameMatches += 1;
      }
      if (nameMatches > 1) {
        // Ambiguous owner — the duplicate itself is a DUPLICATE_TABLE_ENTRY_NAME
        // error; do not guess which record to patch. Never throw.
        return undefined;
      }
      const rec = table.find((r) => r.name === issue.ownerId);
      return rec as unknown as Record<string, unknown> | undefined;
    }
    case "blockDefinition": {
      const block = doc.blocks[issue.ownerId];
      return block as unknown as Record<string, unknown> | undefined;
    }
    case "document":
      return doc as unknown as Record<string, unknown>;
    // linetype (element styleName), blockRecord / layout (handle refs) are not
    // in the healable code set, so they never reach here.
    default:
      return undefined;
  }
}

/**
 * Map a style-table audit `field` to the EXACT table that emitted it, so the
 * owning record is looked up by name within that one table (never across all
 * tables — see the "Standard" name-collision note in `resolveOwnerRoot`).
 *
 * Every healable style-table field is owner-relative and table-unique
 * (`referenceAudit.structural.checkStyleTableRefs` is the single emitter):
 *   - "linetype"        → layers            (Layer.linetype, UNDEFINED_LINETYPE)
 *   - "DIMTXSTY"        → dimensionStyles   (DimStyle text ref, UNDEFINED_TEXT_STYLE)
 *   - "textStyleName"   → multileaderStyles (MLeader text ref, UNDEFINED_TEXT_STYLE)
 *   - "leaderLineType"  → multileaderStyles (MLeader ltype ref, UNDEFINED_LINETYPE)
 *
 * Returns `undefined` for any other field (e.g. block-arrowhead fields, which
 * are UNFIXABLE and never reach heal) so the ref is left as a reported error
 * rather than guessing a table — heal never throws.
 */
function styleTableForField(
  doc: DrawingDocument,
  field: string,
): readonly { name: string }[] | undefined {
  const tables = doc.styleTables;
  switch (field) {
    case "linetype":
      return tables.layers;
    case "DIMTXSTY":
      return tables.dimensionStyles;
    case "textStyleName":
    case "leaderLineType":
      return tables.multileaderStyles;
    default:
      return undefined;
  }
}

/** Re-export the error code set heal targets for consumers / tests. */
export { ReferenceErrorCode };
